import React, { createContext, useContext, useState, useCallback } from 'react';
import type { CartProduct } from './CartContext';

interface WishlistContextType {
  items: CartProduct[];
  addToWishlist: (product: CartProduct) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  compareItems: CartProduct[];
  addToCompare: (product: CartProduct) => void;
  removeFromCompare: (productId: string) => void;
  isInCompare: (productId: string) => boolean;
  isCompareOpen: boolean;
  setIsCompareOpen: (open: boolean) => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartProduct[]>([]);
  const [compareItems, setCompareItems] = useState<CartProduct[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);

  const addToWishlist = useCallback((product: CartProduct) => {
    if (!product || !product.id) {
      console.error('Invalid product passed to addToWishlist');
      return;
    }
    setItems(prev => prev.find(p => p?.id === product.id) ? prev : [...prev, product]);
  }, []);
  
  const removeFromWishlist = useCallback((productId: string) => {
    if (!productId) return;
    setItems(prev => prev.filter(p => p?.id !== productId));
  }, []);
  
  const isInWishlist = useCallback((productId: string) => {
    if (!productId) return false;
    return items?.some(p => p?.id === productId) ?? false;
  }, [items]);

  const addToCompare = useCallback((product: CartProduct) => {
    if (!product || !product.id) {
      console.error('Invalid product passed to addToCompare');
      return;
    }
    setCompareItems(prev => {
      if (prev.find(p => p?.id === product.id)) return prev;
      if (prev.length >= 3) return prev;
      return [...prev, product];
    });
    setIsCompareOpen(true);
  }, []);
  
  const removeFromCompare = useCallback((productId: string) => {
    if (!productId) return;
    setCompareItems(prev => prev.filter(p => p?.id !== productId));
  }, []);
  
  const isInCompare = useCallback((productId: string) => {
    if (!productId) return false;
    return compareItems?.some(p => p?.id === productId) ?? false;
  }, [compareItems]);

  return (
    <WishlistContext.Provider value={{ items, addToWishlist, removeFromWishlist, isInWishlist, compareItems, addToCompare, removeFromCompare, isInCompare, isCompareOpen, setIsCompareOpen }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
};
